import React from 'react';
import { Typography } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

/**
 * StatsCard Component
 * 
 * A modern card component to display financial statistics in a visually appealing way
 * 
 * @param {Object} props Component props
 * @param {string} props.title The title of the statistic
 * @param {string} props.value The formatted value of the statistic
 * @param {string} props.description Additional description text
 * @param {string} props.change Change indicator text (optional)
 * @param {boolean} props.isPositive Whether the change is positive (optional)
 * @param {React.ReactNode} props.icon Icon to display (optional)
 * @param {string} props.color Primary color for the card (defaults to indigo)
 * @returns {JSX.Element} StatsCard component
 */
const StatsCard = ({ 
  title, 
  value, 
  description, 
  change, 
  isPositive, 
  icon: Icon, 
  color = 'indigo',
  tooltip = null
}) => {
  // Define color mappings
  const colorMappings = {
    indigo: {
      bgGradient: 'from-indigo-50 to-indigo-100/90',
      textColor: 'text-indigo-600'
    },
    green: {
      bgGradient: 'from-green-50 to-green-100/90',
      textColor: 'text-green-600'
    },
    red: {
      bgGradient: 'from-red-50 to-red-100/90',
      textColor: 'text-red-600'
    },
    orange: {
      bgGradient: 'from-orange-50 to-orange-100/90',
      textColor: 'text-orange-600'
    },
    blue: {
      bgGradient: 'from-blue-50 to-blue-100/90',
      textColor: 'text-blue-600'
    },
    emerald: {
      bgGradient: 'from-emerald-50 to-emerald-100/90',
      textColor: 'text-emerald-600'
    },
    purple: {
      bgGradient: 'from-purple-50 to-purple-100/90',
      textColor: 'text-purple-600'
    }
  };

  // Use the color from props or default to indigo
  const { bgGradient, textColor } = colorMappings[color] || colorMappings.indigo;

  // Determine trend color and icon
  const trendColor = isPositive ? 'text-green-600' : 'text-red-600';
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;

  return (
    <div className="relative bg-gradient-to-br from-white via-white to-gray-50/95 shadow-[0_8px_25px_-8px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_35px_-8px_rgba(0,0,0,0.4)] rounded-xl p-2 sm:p-3 lg:p-4 border border-white/50 backdrop-blur-xl transition-all duration-300 hover:translate-y-[-2px] min-h-[100px] sm:min-h-[120px] flex flex-col items-center text-center">
      {/* Icon - On its own line at top, centered */}
      {Icon && (
        <div className={`bg-gradient-to-br ${bgGradient} p-1 sm:p-1.5 lg:p-2 rounded-lg shadow-md flex-shrink-0 mb-2 sm:mb-2.5 flex items-center justify-center`}>
          <div className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 flex items-center justify-center">
            <Icon className={`h-full w-full ${textColor}`} />
          </div>
        </div>
      )}
      
      {/* Title - Centered below icon */}
      <div className="mb-1 sm:mb-2 w-full">
        <h3 className="text-[9px] sm:text-xs md:text-sm font-medium text-gray-600 leading-tight text-center px-1 line-clamp-2">
          {title}
        </h3>
      </div>
      
      {/* Value - Centered */}
      <div className="flex-1 flex flex-col justify-center w-full mb-1 sm:mb-1.5">
        <div className={`text-sm sm:text-base md:text-lg lg:text-xl font-bold ${textColor} drop-shadow-sm break-words leading-tight text-center px-1`}>
          {value}
        </div>
      </div>
      
      {/* Description - Centered */}
      {description && (
        <div className="text-[8px] sm:text-[9px] md:text-xs text-gray-500 mb-1 sm:mb-1.5 break-words leading-tight text-center px-1">
          {description}
        </div>
      )}
      
      {/* Change indicator - Centered */}
      {change && (
        <div className="flex items-center justify-center mt-1 sm:mt-2 flex-shrink-0">
          <TrendIcon className={`h-3 w-3 sm:h-4 sm:w-4 mr-1 ${trendColor} flex-shrink-0`} />
          <span className={`${trendColor} font-medium text-[8px] sm:text-[9px] md:text-xs truncate`}>
            {change}
          </span>
        </div>
      )}
    </div>
  );
};

export default StatsCard; 